/**
 * Conversation-learning client: consent for Flux Agent to learn from conversations, the insights
 * it stored, and actions on them (add a suggested custom field, dismiss, delete, or start a
 * conversation from a data request). Every call goes through the shared credentialed `request()`
 * helper and resolves to a {@link FluxAgentResult}; the server scopes every route to the caller.
 *
 * @module apps/web/src/lib/fluxAgentLearningApi
 */

import { API_BASE, request } from '@/lib/api';
import {
  toFluxAgentResult,
  type FluxAgentConversation,
  type FluxAgentResult,
} from '@/lib/fluxAgentApi';

/** Kind of a stored insight, one entry of a distilled conversation. */
export type FluxAgentInsightKind =
  | 'observation'
  | 'feature_prior'
  | 'field_suggestion'
  | 'data_request'
  | 'signal_summary'
  | 'prosody';

/** Overall tone of a conversation segment, from the server's fixed set. */
export type FluxAgentToneLabel =
  | 'neutral'
  | 'calm'
  | 'upbeat'
  | 'excited'
  | 'tired'
  | 'frustrated'
  | 'overwhelmed'
  | 'low';

/** Lifecycle of an insight: new, dismissed by the person, added as a field, or answered. */
export type FluxAgentInsightStatus = 'active' | 'dismissed' | 'applied' | 'answered';

/** Energy axis an insight is about, each scored 0-10. */
export type FluxAgentAxis = 'productivity' | 'rejuvenation' | 'resistance';

/** An inferred energy reading (`EnergyObservation`). */
export interface FluxAgentObservationPayload {
  /** Start of the described window, RFC 3339 with offset. */
  at: string;
  /** Length of the window in minutes, 15-720. */
  windowMinutes: number;
  /** Productivity 0-10, or null when the conversation gave no estimate. */
  productivity: number | null;
  /** Rejuvenation 0-10, or null. */
  rejuvenation: number | null;
  /** Resistance 0-10, or null. */
  resistance: number | null;
  /** Check-in signals as `[snake_case signal, 0-10]` pairs. */
  signals: [string, number][];
  /** Model confidence 0-1. */
  confidence: number;
  /** One-sentence paraphrase of the evidence. */
  basis: string;
}

/** A belief that an input drives one axis (`FeaturePrior`). */
export interface FluxAgentFeaturePriorPayload {
  /** Feature key such as `sleep.minutes_last_night` or `custom.<attribute id>`. */
  feature: string;
  /** Axis the feature affects. */
  axis: FluxAgentAxis;
  /** +1 raises the axis, -1 lowers it, 0 when only relevance is known. */
  direction: -1 | 0 | 1;
  /** How strongly it seems to matter, 0-1. */
  strength: number;
  /** Confidence in the belief, 0-1. */
  confidence: number;
}

/** A custom field Flux Agent suggests adding (`FieldSuggestion`). */
export interface FluxAgentFieldSuggestionPayload {
  /** Field name, 1-48 characters. */
  name: string;
  /** Number with an optional range, or a dropdown of fixed options. */
  fieldType: 'number' | 'dropdown';
  /** Dropdown options, 2-8 entries. */
  options: string[];
  /** Lower bound for a number, or null. */
  min: number | null;
  /** Upper bound for a number, or null. */
  max: number | null;
  /** The quick question asked when logging the value. */
  question: string;
  /** Why it should help prediction. */
  rationale: string;
  /** Axis it is expected to explain. */
  axis: FluxAgentAxis;
}

/** Something Flux Agent wants to ask next (`DataRequest`). */
export interface FluxAgentDataRequestPayload {
  /** The question to ask. */
  question: string;
  /** Why the answer helps. */
  reason: string;
  /** Preferred local hour to ask, 0-23, or null. */
  preferredHour: number | null;
}

/** The energetic summary closing a conversation segment (`SignalSummary`). */
export interface FluxAgentSignalSummaryPayload {
  /** The moment summarized (end of the segment), RFC 3339 with offset. */
  at: string;
  /** How far back the evidence reaches, in minutes. */
  windowMinutes: number;
  /** `[snake_case signal, value 0-10, confidence 0-1]` readings, at most one per signal. */
  signals: [string, number, number][];
  /** Overall tone of the segment. */
  toneLabel: FluxAgentToneLabel;
  /** One-sentence paraphrase of the evidence. */
  basis: string;
}

/** Voice features pooled over a segment (`ProsodySummary`); numbers only, never audio. */
export interface FluxAgentProsodyPayload {
  /** Utterances pooled. */
  utterances: number;
  /** Total utterance time in seconds, pauses included. */
  speechSeconds: number;
  /** Seconds actually spoken. */
  articulationSeconds: number;
  /** Words in the transcripts. */
  words: number;
  /** Words per minute of speech time, or null without words. */
  wordsPerMinute: number | null;
  /** Share of speech time spent in pauses, 0-1. */
  pauseRatio: number;
  /** Mean loudness of spoken frames, dBFS. */
  loudnessMeanDb: number;
  /** Loudness variance, dB². */
  loudnessVarDb: number;
  /** Median pitch in Hz, or null when none was found. */
  pitchMedianHz: number | null;
  /** Pitch spread in semitones, or null. */
  pitchVariabilitySemitones: number | null;
}

/** Fields shared by every stored insight. */
interface FluxAgentInsightBase {
  /** Server-assigned insight id used in `/insights/:id` routes. */
  id: string;
  /** Conversation it was learned in, or null when that conversation was deleted. */
  conversationId: string | null;
  /** Payload confidence, 1 for suggestions and requests. */
  confidence: number;
  /** Observation start in UTC, null for other kinds. */
  observedAt: string | null;
  /** Lifecycle status. */
  status: FluxAgentInsightStatus;
  /** Consent version in force when it was stored. */
  consentVersion: string;
  /** UTC creation timestamp as stored by D1. */
  createdAt: string;
  /** UTC timestamp of the last status change. */
  updatedAt: string;
}

/** One stored insight, discriminated by `kind`. */
export type FluxAgentInsight =
  | (FluxAgentInsightBase & {
      /** Discriminant. */
      kind: 'observation';
      /** Inferred reading. */
      payload: FluxAgentObservationPayload;
    })
  | (FluxAgentInsightBase & {
      /** Discriminant. */
      kind: 'feature_prior';
      /** Feature belief. */
      payload: FluxAgentFeaturePriorPayload;
    })
  | (FluxAgentInsightBase & {
      /** Discriminant. */
      kind: 'field_suggestion';
      /** Suggested field. */
      payload: FluxAgentFieldSuggestionPayload;
    })
  | (FluxAgentInsightBase & {
      /** Discriminant. */
      kind: 'data_request';
      /** Question to ask later. */
      payload: FluxAgentDataRequestPayload;
    })
  | (FluxAgentInsightBase & {
      /** Discriminant. */
      kind: 'signal_summary';
      /** The 18-signal energetic summary. */
      payload: FluxAgentSignalSummaryPayload;
    })
  | (FluxAgentInsightBase & {
      /** Discriminant. */
      kind: 'prosody';
      /** Pooled voice numbers. */
      payload: FluxAgentProsodyPayload;
    });

/** Consent state and how much is stored. */
export interface FluxAgentLearningState {
  /** True unless the person turned learning off (it is on by default under the Terms of Use). */
  enabled: boolean;
  /** Always true: learning starts on. */
  defaultEnabled: boolean;
  /** `default` until the person flips the switch, then `choice`. */
  source: 'default' | 'choice';
  /** Consent version the switch accepts, e.g. `flux-agent-learning-v1`. */
  consentVersion: string;
  /** Stored insights per kind, in every status. */
  counts: Record<FluxAgentInsightKind, number>;
}

/** A custom attribute row created by applying a suggestion, as stored by the server. */
export interface FluxAgentCreatedAttribute {
  /** Attribute id. */
  attr_id: string;
  /** Field name. */
  name: string;
  /** Stored type: `float` for numbers, `enum` for dropdowns. */
  data_type: string;
}

/** Filters for {@link fluxAgentLearningApi.listInsights}. */
export interface FluxAgentInsightFilter {
  /** Only this kind. */
  kind?: FluxAgentInsightKind;
  /** Only this status. */
  status?: FluxAgentInsightStatus;
}

/** Send a request to `/flux-agent{endpoint}` and fold the envelope into a result. */
async function call<T>(endpoint: string, options: RequestInit = {}): Promise<FluxAgentResult<T>> {
  return toFluxAgentResult(await request<T>(`/flux-agent${endpoint}`, options));
}

/** Encode an insight id for a path segment. */
function insightPath(id: string, action = ''): string {
  return `/insights/${encodeURIComponent(id)}${action}`;
}

/** Conversation-learning route methods; each resolves to a result and never throws for HTTP failures. */
export const fluxAgentLearningApi = {
  /** GET `/learning`: whether learning is on, the consent version, and counts by kind. */
  getLearning: () => call<FluxAgentLearningState>('/learning'),
  /** PUT `/learning` with `{ enabled }`. Turning it off keeps what is stored. */
  setLearning: (enabled: boolean) =>
    call<FluxAgentLearningState>('/learning', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),
  /** GET `/insights?kind=&status=`: the caller's insights, newest first (at most 200). */
  listInsights: (filter: FluxAgentInsightFilter = {}) => {
    const query = new URLSearchParams();
    if (filter.kind) query.set('kind', filter.kind);
    if (filter.status) query.set('status', filter.status);
    const suffix = query.toString() ? `?${query}` : '';
    return call<{ insights: FluxAgentInsight[] }>(`/insights${suffix}`);
  },
  /** DELETE `/insights/:id`: permanently removes one insight. */
  deleteInsight: (id: string) => call<{ deleted: number }>(insightPath(id), { method: 'DELETE' }),
  /** DELETE `/insights`: permanently removes every stored insight. */
  deleteAllInsights: () => call<{ deleted: number }>('/insights', { method: 'DELETE' }),
  /** POST `/insights/:id/apply`: adds a suggested custom field and marks it applied. */
  applySuggestion: (id: string) =>
    call<{ attribute: FluxAgentCreatedAttribute; insight: FluxAgentInsight }>(
      insightPath(id, '/apply'),
      { method: 'POST' }
    ),
  /** POST `/insights/:id/dismiss`: hides an active suggestion or request. */
  dismissInsight: (id: string) =>
    call<{ insight: FluxAgentInsight }>(insightPath(id, '/dismiss'), { method: 'POST' }),
  /**
   * POST `/conversations/:id/end` without waiting: the segment's energetic summary is produced
   * now. A keep-alive `fetch` rather than `sendBeacon`, because a beacon cannot carry the CSRF
   * header the API requires; keep-alive requests survive page unload the same way.
   */
  endConversation: (id: string): void => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Fluxability-CSRF': '1',
    };
    try {
      const token = localStorage.getItem('flexiplanner-session-token');
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      // Cookie authentication still applies.
    }
    void fetch(`${API_BASE}/flux-agent/conversations/${encodeURIComponent(id)}/end`, {
      method: 'POST',
      body: '{}',
      headers,
      credentials: 'include',
      keepalive: true,
    }).catch(() => undefined);
  },
  /** POST `/insights/:id/start`: opens a conversation whose first message is the request's question. */
  startDataRequest: (id: string) =>
    call<{ conversation: FluxAgentConversation }>(insightPath(id, '/start'), { method: 'POST' }),
};
